import { useEffect, useState } from 'react';
import { api, ApiError } from '../../api';
import { useToast } from '../../toast';
import { ChannelToggles, type Channel } from '../../channels';
import { useWorkspace } from '../Workspace';

/**
 * The two decisions that hold for the whole workspace: which ways in to Salesforce it allows at all, and
 * how long the session log is kept. Each sits in its own card with its own save, because they are made
 * by different people (an admin and an owner) and saving one should never quietly save the other.
 */
export function WorkspaceSettings() {
  const { tenantId, role, channels: loaded, auditRetention, reload } = useWorkspace();
  const [channels, setChannels] = useState<Channel[]>(loaded);
  const [months, setMonths] = useState(auditRetention.months);
  const [savingRetention, setSavingRetention] = useState(false);
  const [saving, setSaving] = useState(false);
  const toast = useToast();

  // The layout owns the loaded value, so a save elsewhere or a reload has to land here too.
  useEffect(() => {
    setChannels(loaded);
  }, [loaded]);

  useEffect(() => {
    setMonths(auditRetention.months);
  }, [auditRetention.months]);

  // Every whole month this workspace is allowed to choose between.
  const choices: number[] = [];
  for (let m = auditRetention.floorMonths; m <= auditRetention.maxMonths; m += 1) choices.push(m);
  const atCeiling = auditRetention.maxMonths <= auditRetention.floorMonths;

  return (
    <>
      <div className="card">
        <h3>What this workspace allows</h3>
        <p className="muted" style={{ marginTop: 0 }}>
          The ceiling for every org connected here. An org can narrow this but never widen it, so turning
          something off withdraws it everywhere at once.
        </p>
        <ChannelToggles value={channels} onChange={setChannels} disabled={saving} />
        <div className="actions">
          <button
            type="button"
            disabled={saving || !channels.length}
            onClick={() => {
              setSaving(true);
              api<{ channels: Channel[] }>(`/api/tenants/${tenantId}/channels`, { method: 'PATCH', body: { channels } })
                .then(async (r) => {
                  setChannels(r.channels);
                  toast.ok('Saved.');
                  await reload();
                })
                .catch((err: unknown) => toast.error(err instanceof ApiError ? err.message : 'Could not save'))
                .finally(() => setSaving(false));
            }}
          >
            Save
          </button>
          {!channels.length && <span className="small muted" style={{ alignSelf: 'center' }}>Choose at least one.</span>}
        </div>
      </div>

      <div className="card">
        <h3>How long the session log is kept</h3>
        <p className="muted" style={{ marginTop: 0 }}>
          Every session Kruvi Access mints here is recorded with the person, the customer org and the reason. Those
          records are deleted once they reach this age, and nothing else ever removes one.
        </p>
        {role === 'owner' ? (
          <>
            <div className="row" style={{ alignItems: 'flex-end' }}>
              <label>
                Keep for
                <select
                  value={months}
                  disabled={savingRetention || atCeiling}
                  onChange={(e) => setMonths(Number(e.target.value))}
                >
                  {choices.map((m) => (
                    <option key={m} value={m}>{m} months</option>
                  ))}
                </select>
              </label>
              <button
                type="button"
                className="secondary"
                disabled={savingRetention || months === auditRetention.months}
                onClick={() => {
                  setSavingRetention(true);
                  api<{ months: number }>(`/api/tenants/${tenantId}/audit-retention`, {
                    method: 'PATCH',
                    body: { months },
                  })
                    .then(async (r) => {
                      toast.ok(`The session log is now kept for ${r.months} months.`);
                      await reload();
                    })
                    .catch((err: unknown) =>
                      toast.error(err instanceof ApiError ? err.message : 'Could not save'),
                    )
                    .finally(() => setSavingRetention(false));
                }}
              >
                Save
              </button>
            </div>
            <p className="small muted" style={{ marginBottom: 0 }}>
              {auditRetention.floorMonths === auditRetention.maxMonths
                ? `This workspace keeps ${auditRetention.floorMonths} months.`
                : `Anything from ${auditRetention.floorMonths} to ${auditRetention.maxMonths} months.`}{' '}
              The floor is ours, not a setting: an audit log its own subject can shorten at will is not
              evidence of anything. Need to keep more than {auditRetention.maxMonths} months?{' '}
              <a href={`mailto:${auditRetention.contact}?subject=Audit%20log%20retention`}>Contact us</a>.
            </p>
          </>
        ) : (
          <p className="small muted" style={{ marginBottom: 0 }}>
            Kept for {auditRetention.months} months. An owner can change this.
          </p>
        )}
      </div>
    </>
  );
}
